import {useFormik} from 'formik'
import * as Yup from 'yup'
import {IAllUserNode, IResEditMember} from '../../../types/users'
import {EDIT_USER, EDIT_USER_STR} from '../../../gql/mutations/userMutations'
import {useMutation, useQuery} from '@apollo/client'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {Col, Form, Row} from 'react-bootstrap'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'
import {parseId} from '../../../helpers'
import {IResRolesGroups} from '../../../types/roles'
import {GET_ROLES_GROUP} from '../../../gql/queries/rolesGroupQuery'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {prependBackendUrl} from '../../../gql/client'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {useGetUser} from '../../../store/userStore'

interface IPropfileProps {
  node: IAllUserNode
  refetch: () => void
}

const INIT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  cellphone: '',
  applyDeductions: '',
  rol: '',
  npn: '',
  organizationId: '',
  avatar: null as File | null,

}
const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  cellphone: Yup.string(),
  applyDeductions: Yup.string(),
})

export const ProfileDetailsSetting: React.FC<IPropfileProps> = ({node, refetch}) => {
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const normalizeId = (id?: string | number | null) => (id ? `${parseId(id)}` : '')

  // Get current user's role
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const currentUserId = currentUser?.id
  // Check user roles
  const isAgente = userGroups.includes('Agente')
  const isAsesor = userGroups.includes('Asesor')
  const isSupervisor = userGroups.includes('Supervisor')
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')
  const isEditingSelf = !!(node && currentUserId && node.id === currentUserId)

  // Determine if role selector should be disabled
  const isRoleSelectorDisabled = !!(isAgente || isAsesor) && isEditingSelf

  // Determine if organization selector should be disabled
  const isOrganizationSelectorDisabled = !isSuperAdministrador

  const {
    data: dataRoles,
    loading: loadingRoles,
    error: errorRoles,
  } = useQuery<IResRolesGroups>(GET_ROLES_GROUP, { fetchPolicy: 'no-cache' })

  // Query for organizations
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: { first: 100 },
    fetchPolicy: 'no-cache',
  })

  // Para actualizar sin archivo (mutation normal de Apollo)
  const [gqlEdit, { data, loading, error }] = useMutation<IResEditMember>(EDIT_USER)

  // Para actualizar con archivo (fetch directo con FormData)
  const [uploadFile, {data: dataUpload, loading: loadingUpload}] =
    useUploadFile<IResEditMember>()

  const formik = useFormik({
    initialValues: INIT_VALUES,
    validationSchema,

    onSubmit: async (v, o) => {
      const applyDeductionsValue = v.applyDeductions
        ? v.applyDeductions === 'yes'
        : node.applyDeductions

      const userData = {
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        cellphone: v.cellphone,
        npn: v.npn,
        applyDeductions: applyDeductionsValue,
        isActive: true,
        primaryGroup: v.rol ? parseId(v.rol) : undefined,
        organizationId: v.organizationId ? parseId(v.organizationId) : undefined,

      }

      // Si hay un archivo, usar FormData (como en ModalAddDocsPolicy)
      if (v.avatar) {
        const formdata = new FormData()
        formdata.append('0', v.avatar, v.avatar.name)

        const map = { '0': ['variables.userData.avatar'] }

        const obj = {
          query: EDIT_USER_STR,
          variables: {
            userData: {
              ...userData,
              avatar: null, // Se reemplazará con el archivo del FormData
            },
            userId: parseId(node.id),
          },
        }

        formdata.append('map', JSON.stringify(map))
        formdata.append('operations', JSON.stringify(obj))

        uploadFile(formdata)
      } else {
        // Sin archivo, usar mutation normal de Apollo
        gqlEdit({
          variables: {
            userData,
            userId: parseId(node.id),
          },
        })
      }
    },
  })

  useEffect(() => {
    // Si no es SuperAdministrador y el usuario editado no tiene organización,
    // usar la organización del usuario actual logueado
    const organizationIdValue = node.organization
      ? normalizeId(node.organization.id)
      : (!isSuperAdministrador && currentUser?.organization?.id
        ? normalizeId(currentUser.organization.id)
        : '')

    formik.setValues({
      applyDeductions: node.applyDeductions ? 'yes' : 'no',
      cellphone: node.cellphone,
      email: node.email,
      firstName: node.firstName,
      lastName: node.lastName,
      rol: node.primaryGroup ? node.primaryGroup.id : '',
      npn: node.npn,
      organizationId: organizationIdValue,
      avatar: null, // No cargamos el avatar existente como File, solo lo mostramos en el preview

    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, isSuperAdministrador, currentUser])

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error.message}`)
    }
  }, [error])
  useEffect(() => {
    if (errorRoles) toast.error(`Error: ${errorRoles.message}`)
  }, [errorRoles])
  useEffect(() => {
    if (errorOrganizations) toast.error(`Error: ${errorOrganizations.message}`)
  }, [errorOrganizations])

  useEffect(() => {
    if (!data) return
    if (data?.updateUserMutation?.user?.id) {
      toast.success('User updated successfully')
      refetch()
      formik.setFieldValue('avatar', null)
      setAvatarPreview(null)
    } else if (data?.updateUserMutation.errors?.length > 0) {
      toast.error(`Error: ${data?.updateUserMutation.errors[0]?.message || 'Something wrong!'}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (!dataUpload) return
    if (dataUpload?.updateUserMutation?.user?.id) {
      toast.success('User and avatar updated successfully')
      refetch()
      formik.setFieldValue('avatar', null)
      setAvatarPreview(null)
    } else if (dataUpload?.updateUserMutation?.errors?.length > 0) {
      toast.error(`Error: ${dataUpload?.updateUserMutation?.errors[0]?.message || 'Something wrong!'}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpload])

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_profile_details'
        aria-expanded='true'
        aria-controls='kt_account_profile_details'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Profile Details</h3>
        </div>
      </div>

      <div id='kt_account_profile_details' className='collapse show'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Avatar</label>
              <div className='col-lg-8'>
                <div className='image-input image-input-outline' data-kt-image-input='true'>
                  <div
                    className='image-input-wrapper w-125px h-125px'
                    style={{
                      backgroundImage: `url(${avatarPreview ||
                        (node.avatar ? prependBackendUrl(node.avatar) : toAbsoluteUrl('/media/avatars/blank.png'))
                        })`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  ></div>
                  <label
                    className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                    data-kt-image-input-action='change'
                    data-bs-toggle='tooltip'
                    title='Change avatar'
                  >
                    <i className='bi bi-pencil-fill fs-7'></i>
                    <input
                      type='file'
                      name='avatar'
                      accept='.png, .jpg, .jpeg, .webp'
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          formik.setFieldValue('avatar', file)
                          // Preview de la imagen
                          const reader = new FileReader()
                          reader.onloadend = () => {
                            setAvatarPreview(reader.result as string)
                          }
                          reader.readAsDataURL(file)
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {(avatarPreview || node.avatar) && (
                    <span
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='remove'
                      data-bs-toggle='tooltip'
                      title='Remove avatar'
                      onClick={() => {
                        formik.setFieldValue('avatar', null)
                        setAvatarPreview(null)
                      }}
                    >
                      <i className='bi bi-x fs-2'></i>
                    </span>
                  )}
                </div>
                <div className='form-text'>Allowed file types: png, jpg, jpeg, webp.</div>
              </div>
            </div>

            <Row className='g-2'>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    First name <small className='text-danger'>(required)</small>
                  </Form.Label>
                  <Form.Control
                    placeholder='First name'
                    name='firstName'
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.firstName && formik.touched.firstName)}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    Last name <small className='text-danger'>(required)</small>
                  </Form.Label>
                  <Form.Control
                    placeholder='Last name'
                    name='lastName'
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.lastName && formik.touched.lastName)}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    Email address <small className='text-danger'>(required)</small>
                  </Form.Label>
                  <Form.Control
                    placeholder='Email address'
                    type='email'
                    name='email'
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.email && formik.touched.email)}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>Cellphone number</Form.Label>
                  <Form.Control
                    placeholder='Cellphone'
                    type='tel'
                    name='cellphone'
                    value={formik.values.cellphone}
                    onChange={(e) => {
                      let v = e.target.value
                      // Permite "+" solo al inicio y dígitos; elimina otros caracteres
                      v = v.replace(/(?!^\+)[^\d]/g, '')        // quita todo lo que no sea dígito excepto un '+' inicial
                      v = v.replace(/(?!^)\+/g, '')             // quita '+' extra si los hay
                      formik.setFieldValue('cellphone', v)
                    }}
                    maxLength={15}
                    isInvalid={Boolean(formik.errors.cellphone && formik.touched.cellphone)}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.cellphone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    NPN
                  </Form.Label>
                  <Form.Control
                    placeholder='NPN'
                    name='npn'
                    value={formik.values.npn}
                    onChange={formik.handleChange}

                  />

                </Form.Group>
                {/* <Form.Group>
                  <Form.Label>
                    ¿Apply deductions? <small className='text-danger'>(required)</small>
                  </Form.Label>
                  <Form.Select
                    name='applyDeductions'
                    value={formik.values.applyDeductions}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(
                      formik.errors.applyDeductions && formik.touched.applyDeductions
                    )}
                  >
                    <option value=''>Select One</option>
                    {['yes', 'no'].map((text) => (
                      <option key={text} value={text}>
                        {text}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.applyDeductions}
                  </Form.Control.Feedback>
                </Form.Group>*/}
                {/* <small>
                <span className='text-primary' style={{textDecoration: 'underline'}}>
                  Click here
                </span>{' '}
                to learn more about our access roles.
              </small> */}
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    Rol <small className='text-danger'>(required)</small>
                  </Form.Label>
                  <Form.Select
                    name='rol'
                    value={formik.values.rol}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.rol && formik.touched.rol)}
                    disabled={loadingRoles || isRoleSelectorDisabled}
                  >
                    {loading ? (
                      <option value=''>Loading...</option>
                    ) : (
                      <option value=''>Select One</option>
                    )}
                    {dataRoles?.allGroups.edges
                      .filter(({ node }) => {
                        // Si no es SuperAdministrador, no puede asignar rol SuperAdministrador
                        if (!isSuperAdministrador && node.name === 'SuperAdministrador') {
                          return false
                        }
                        // Si es Supervisor, no puede crear o asignar rol de Administrador
                        if (isSupervisor && node.name === 'Administrador') {
                          return false
                        }
                        return true
                      })
                      .map(({ node }) => (
                        <option key={node.id} value={node.id}>
                          {node.name}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>{formik.errors.rol}</Form.Control.Feedback>
                  {isRoleSelectorDisabled && (
                    <Form.Text className='text-muted'>
                      You don't have permissions to change your own role
                    </Form.Text>
                  )}
                </Form.Group>
                {/* <small>
                <span className='text-primary' style={{textDecoration: 'underline'}}>
                  Click here
                </span>{' '}
                to learn more about our access roles.
              </small> */}
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>Organization</Form.Label>
                  <Form.Select
                    name='organizationId'
                    value={formik.values.organizationId}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(
                      formik.errors.organizationId && formik.touched.organizationId
                    )}
                    disabled={loadingOrganizations || isOrganizationSelectorDisabled}
                  >
                    {loadingOrganizations ? (
                      <option value=''>Loading...</option>
                    ) : (
                      <option value=''>Select One</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({ node }: any) => {
                      const value = normalizeId(node.id)
                      return (
                        <option key={node.id} value={value}>
                          {node.name}
                        </option>
                      )
                    })}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.organizationId}
                  </Form.Control.Feedback>
                  {isOrganizationSelectorDisabled && (
                    <Form.Text className='text-muted'>
                      Only SuperAdministrador can change the organization
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              {/* <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Preferred language <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Select>
                  <option>Spanish</option>
                  <option>English</option>
                </Form.Select>
              </Form.Group>
            </Col> */}
              {/* <Col className='mt-5'>
                <Form.Check
                  type='checkbox'
                  label={
                    <span className='text-dark'>
                      Check this box to send this user an email invitation.
                    </span>
                  }
                  defaultChecked
                />
              </Col> */}
            </Row>
          </div>

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={loading || loadingUpload}
            >
              {!(loading || loadingUpload) && 'Save Changes'}
              {(loading || loadingUpload) && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
